import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  List,
  ListItem,
  Chip
} from '@mui/material';
import {
  History as HistoryIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  VisibilityOutlined as PreviewIcon,
  SearchOutlined as CompareIcon,
  ArrowBack as ArrowBackIcon,
  RestoreOutlined as RestoreIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { VersionHistoryDialogProps } from './types';
import { getLanguageDisplayName } from './utils';
import { StatementXmlPreview } from '../statement-nodes';

export const VersionHistoryDialog: React.FC<VersionHistoryDialogProps> = ({
  open,
  onClose,
  language,
  versionHistory,
  loading,
  previewVersion,
  previewLoading,
  compareDiff,
  compareLoading,
  canRollback,
  rollbackLoading,
  onPreviewVersion,
  onCompareWithCurrent,
  onRollbackVersion,
  onBackToVersions
}) => {
  const { t } = useTranslation();
  const [rollbackConfirm, setRollbackConfirm] = React.useState<{
    version: number;
    params: { version?: number; version_id?: string };
  } | null>(null);
  const isPreviewMode = previewLoading || !!previewVersion;
  const isCompareMode = compareLoading || !!compareDiff;
  const isDetailsMode = isPreviewMode || isCompareMode;
  const currentVersionNumber = versionHistory[0]?.version ?? null;
  const canRestorePreviewVersion = canRollback && !!previewVersion && previewVersion.version !== currentVersionNumber;
  const canRestoreComparedVersion = canRollback && !!compareDiff && compareDiff.from_version !== compareDiff.to_version;
  const restoreButtonLabel = rollbackLoading ? t('productDetails.restoringVersion') : t('productDetails.restoreVersion');

  const comparedFrom = compareDiff
    ? versionHistory.find((item) => item.version === compareDiff.from_version) ?? null
    : null;
  const comparedTo = compareDiff
    ? versionHistory.find((item) => item.version === compareDiff.to_version) ?? null
    : null;

  const openRollbackConfirm = (version: number, params: { version?: number; version_id?: string }) => {
    setRollbackConfirm({ version, params });
  };

  const closeRollbackConfirm = () => {
    if (rollbackLoading) return;
    setRollbackConfirm(null);
  };

  const confirmRollback = async () => {
    if (!rollbackConfirm || rollbackLoading) return;
    await Promise.resolve(onRollbackVersion(rollbackConfirm.params));
    setRollbackConfirm(null);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: { xs: 520, md: 620 }
          }
        }}
      >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <HistoryIcon />
            <Box>
              <Typography variant="h6">
                {isCompareMode
                  ? t('productDetails.compareHeader', {
                      fromVersion: compareDiff?.from_version ?? '-',
                      toVersion: compareDiff?.to_version ?? '-',
                    })
                  : isPreviewMode
                  ? t('productDetails.previewHeader', { version: previewVersion?.version ?? '-' })
                  : t('productDetails.versionHistoryTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getLanguageDisplayName(language)} ({language.toUpperCase()})
              </Typography>
            </Box>
          </Stack>
          {isDetailsMode && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBackToVersions}
              disabled={previewLoading || compareLoading}
            >
              {t('productDetails.backToVersions')}
            </Button>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {compareLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography>{t('productDetails.loadingVersionComparison')}</Typography>
          </Box>
        ) : compareDiff ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderWidth: 2,
                  borderRadius: 1.5,
                  p: 2,
                  bgcolor: 'grey.50',
                  minHeight: 300,
                }}
              >
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('productDetails.comparedVersionLabel', { version: compareDiff.from_version })}
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <TimeIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        {comparedFrom ? new Date(comparedFrom.created_at).toLocaleString() : '-'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <PersonIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        {comparedFrom?.created_by ?? '-'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <StatementXmlPreview
                    value={compareDiff.from_text}
                    sx={{
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                      color: 'text.primary',
                    }}
                  />

                  {canRestoreComparedVersion && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<RestoreIcon />}
                        onClick={() => openRollbackConfirm(compareDiff.from_version, { version: compareDiff.from_version })}
                        disabled={rollbackLoading}
                      >
                        {restoreButtonLabel}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'success.main',
                  borderWidth: 2,
                  borderRadius: 1.5,
                  p: 2,
                  bgcolor: 'grey.50',
                  minHeight: 300,
                }}
              >
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('productDetails.currentComparedVersionLabel', { version: compareDiff.to_version })}
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <TimeIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        {comparedTo ? new Date(comparedTo.created_at).toLocaleString() : '-'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <PersonIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        {comparedTo?.created_by ?? '-'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <StatementXmlPreview
                    value={compareDiff.to_text}
                    sx={{
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                      color: 'text.primary',
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>
        ) : previewLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography>{t('productDetails.loadingVersionPreview')}</Typography>
          </Box>
        ) : previewVersion ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('productDetails.versionLabel', { version: previewVersion.version })}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <TimeIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    {new Date(previewVersion.created_at).toLocaleString()}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PersonIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    {previewVersion.created_by}
                  </Typography>
                </Stack>
              </Stack>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 2,
                  bgcolor: 'grey.50',
                  minHeight: 220,
                }}
              >
                <StatementXmlPreview
                  value={previewVersion.text}
                  sx={{
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    color: 'text.primary',
                  }}
                />
              </Box>

              {canRestorePreviewVersion && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<RestoreIcon />}
                    onClick={() => openRollbackConfirm(previewVersion.version, { version_id: previewVersion.id })}
                    disabled={rollbackLoading}
                  >
                    {restoreButtonLabel}
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>
        ) : loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography>{t('productDetails.loadingVersionHistory')}</Typography>
          </Box>
        ) : versionHistory.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography color="text.secondary">{t('productDetails.noVersionHistory')}</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {versionHistory.map((version, index) => (
              <ListItem key={version.id} disableGutters sx={{ p: 0 }}>
                <Box
                  sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', md: 'center' },
                      gap: 2,
                    }}
                  >
                    <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight={600}>
                          {t('productDetails.versionLabel', { version: version.version })}
                        </Typography>
                      </Stack>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <TimeIcon sx={{ fontSize: 16 }} />
                          <Typography variant="caption">
                            {new Date(version.created_at).toLocaleString()}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <PersonIcon sx={{ fontSize: 16 }} />
                          <Typography variant="caption">
                            {version.created_by}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>

                    <Stack
                      spacing={1}
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        minWidth: { md: 380 },
                        alignItems: { xs: 'stretch', md: 'flex-end' },
                      }}
                    >
                      {index === 0 && (
                        <Chip size="small" label={t('productDetails.currentVersion')} color="primary" />
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          width: '100%',
                          justifyContent: { xs: 'stretch', md: 'flex-end' },
                          flexWrap: 'wrap',
                        }}
                      >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={() => onPreviewVersion(version.version)}
                        sx={{ minWidth: { xs: '100%', sm: 140 } }}
                      >
                        {t('productDetails.previewVersion')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CompareIcon />}
                        onClick={() => onCompareWithCurrent(version.version)}
                        disabled={index === 0}
                        sx={{ minWidth: { xs: '100%', sm: 220 } }}
                      >
                        {t('productDetails.compareWithCurrent')}
                      </Button>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          {t('productDetails.close')}
        </Button>
      </DialogActions>
      </Dialog>

      <Dialog
        open={!!rollbackConfirm}
        onClose={closeRollbackConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('productDetails.restoreConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('productDetails.restoreConfirmMessage', {
              version: rollbackConfirm?.version ?? '-',
              language: language.toUpperCase(),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRollbackConfirm} disabled={rollbackLoading}>
            {t('productDetails.no')}
          </Button>
          <Button onClick={confirmRollback} variant="contained" color="warning" disabled={rollbackLoading}>
            {t('productDetails.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
